// The library (docs/tune-library-plan.md §5, step D) as a tune SOURCE: tunes
// an admin promoted from what people save, readable by everyone (migration
// 0009's `library_tunes`). The openbook/realbook pattern — a list fetched
// once per session, each tune loaded on demand — except the list lives in
// Supabase rather than a static manifest, so an admin's promotion or removal
// refreshes it (`refreshLibraryTunes`).
//
// A library tune is stamped `library-<row id>` on load: that id is what keys
// per-tune band memory, account settings and house defaults, so a promoted
// tune carries a house arrangement like any other tune.

import { useEffect, useSyncExternalStore } from 'react';
import { bootAuth, getSupabaseRaw } from '@/auth/session';
import type { Score } from '@/music/score/types';
import { checkSavedTunes } from '@/storage/savedTunes';

const TABLE = 'library_tunes';

export interface LibraryTuneEntry {
  id: string;
  title: string;
  composer?: string;
  /** The name the admin chose to credit ("Sam, Tuesday class"). */
  credit?: string;
  /** Carries a written line (the server's generated column: any part). */
  melody: boolean;
  /** The tune's feel label, read out of the doc by the list query. */
  style?: string;
  createdAt: string;
}

type Row = Record<string, unknown>;
type Result = { data: Row[] | null; error: { message: string } | null };

/** The slice of supabase-js this module uses (tests fake it). */
export interface LibraryClient {
  from(table: string): {
    select(columns: string): PromiseLike<Result> & {
      eq(column: string, value: string): PromiseLike<Result>;
    };
  };
}

let testClient: LibraryClient | null = null;

async function client(): Promise<LibraryClient | null> {
  if (testClient) return testClient;
  await bootAuth();
  return getSupabaseRaw() as LibraryClient | null;
}

const str = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() ? v.trim() : undefined;

function entryOf(row: Row): LibraryTuneEntry | null {
  const id = str(row.id);
  const title = str(row.title);
  if (!id || !title) return null;
  return {
    id,
    title,
    composer: str(row.composer),
    credit: str(row.credit),
    melody: row.melody === true,
    style: str(row.style),
    createdAt: typeof row.created_at === 'string' ? row.created_at : '',
  };
}

// --- the list, shared by every consumer -------------------------------------

let entries: LibraryTuneEntry[] = [];
let pending: Promise<LibraryTuneEntry[]> | null = null;
const listeners = new Set<() => void>();

function publish(next: LibraryTuneEntry[]) {
  entries = next;
  for (const listener of listeners) listener();
}

/** Every library tune, sorted by title. [] without a backend; what it had
 *  before when the connection fails (and the next ask tries again). */
export function libraryTunes(): Promise<LibraryTuneEntry[]> {
  pending ??= (async () => {
    try {
      const c = await client();
      if (!c) return [];
      const { data, error } = await c
        .from(TABLE)
        .select('id, title, composer, credit, melody, style:doc->>style, created_at');
      if (error || !data) throw new Error(error?.message ?? 'no data');
      const list = data
        .map(entryOf)
        .filter((e): e is LibraryTuneEntry => e !== null)
        .sort((a, b) => a.title.localeCompare(b.title));
      publish(list);
      return list;
    } catch {
      pending = null;
      return entries;
    }
  })();
  return pending;
}

/** Fetch the list again (an admin promoted or removed a tune). */
export function refreshLibraryTunes(): Promise<LibraryTuneEntry[]> {
  pending = null;
  return libraryTunes();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The library list, live. Fetched the first time an enabled caller asks, so
 *  a surface that is mounted but closed (the palette) costs nothing. */
export function useLibraryTunes(enabled = true): LibraryTuneEntry[] {
  const list = useSyncExternalStore(subscribe, () => entries);
  useEffect(() => {
    if (enabled) void libraryTunes();
  }, [enabled]);
  return list;
}

// --- one tune ----------------------------------------------------------------

/** Load one library tune as a Score, guarded like any tune another device wrote. */
export async function loadLibraryScore(entry: { id: string }): Promise<Score> {
  const c = await client();
  if (!c) throw new Error('The library is not available.');
  const { data, error } = await c.from(TABLE).select('id, title, composer, doc').eq('id', entry.id);
  if (error) throw new Error(error.message);
  const row = data?.[0];
  if (!row || !row.doc || typeof row.doc !== 'object') {
    throw new Error('That tune is no longer in the library.');
  }
  const id = `library-${entry.id}`;
  // The row's title and composer are what the library lists (an admin may
  // have tidied them at promotion), so the tune reads as it was listed.
  const raw = {
    ...(row.doc as Score),
    id,
    title: str(row.title) ?? (row.doc as Score).title,
    composer: str(row.composer) ?? (row.doc as Score).composer,
  };
  const [score] = checkSavedTunes([raw]).kept;
  if (!score) throw new Error('That library tune could not be read.');
  return score;
}

/** The library row id behind a loaded tune's id, or null. */
export function libraryIdOf(scoreId: string | undefined): string | null {
  return scoreId?.startsWith('library-') ? scoreId.slice('library-'.length) : null;
}

// --- test seams ----------------------------------------------------------------

export function __setLibraryTestClient(c: LibraryClient | null): void {
  testClient = c;
}

export function __resetLibraryTunesForTests(): void {
  entries = [];
  pending = null;
}
